import { createApiRef } from "@backstage/core";
import { DiscoveryApi, IdentityApi } from "@backstage/core-plugin-api";
import axios, { AxiosResponse } from "axios";
import {
  ReviewManualDetails,
  ReviewManualDetailsDBResponse,
} from "@internal/plugin-ops-review-common";

export const opsReviewApiRef = createApiRef<OpsReviewApi>({
  id: "plugin.ops-review-api.service",
  description: "Used by the OpsReview plugin to make requests to the backend",
});

type Options = {
  discoveryApi: DiscoveryApi;
  identityApi: IdentityApi;
};

export class OpsReviewApi {
  private readonly identityApi: IdentityApi;
  private readonly discoveryApi: DiscoveryApi;

  constructor(options: Options) {
    this.identityApi = options.identityApi;
    this.discoveryApi = options.discoveryApi;
  }

  /**
   * Retrieve the url for the api
   */
  private async getUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl("opsreview-api");
  }

  /**
   * Compile the headers to be sent to the backend
   */
  private async headers() {
    const { token } = await this.identityApi.getCredentials();
    return {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }

  /**
   * Retrieve a reviewed incident by incidentId
   */
  public async getReviewedIncident(incidentId: string) {
    const response = await axios.get(
      `${await this.getUrl()}/getReviewedIncident?incidentId=${incidentId}`,
      { headers: await this.headers() }
    );
    return response.data;
  }

  public async getHistoricalReferenceDates() {
    const response = await axios.get(
      `${await this.getUrl()}/getHistoricalReferenceDates`,
      { headers: await this.headers() }
    );
    return response.data;
  }

  /**
   * Fetch Ownership
   */
  public async fetchOwnership() {
    const response = await axios.get(`${await this.getUrl()}/fetchOwnership`, {
      headers: await this.headers(),
    });
    return response.data;
  }

  /**
   * Get historical reference by date
   */
  public async getHistoricalReference({ date }: { date: string }) {
    const response = await axios.get(
      `${await this.getUrl()}/getHistoricalReference?date=${date}`,
      { headers: await this.headers() }
    );
    return response.data;
  }

  /**
   * Get reviewed Incidents
   */
  public async listReviewedIncidents() {
    const response = await axios.get(
      `${await this.getUrl()}/listReviewedIncidents`,
      { headers: await this.headers() }
    );
    return response.data;
  }

  /**
   * Set data for a reviewed incident
   */
  public async setReviewedIncident(incidentId: string) {
    const response = await axios.post(
      `${await this.getUrl()}/setReviewedIncident`,
      { incidentId },
      { headers: await this.headers() }
    );
    return response.data;
  }

  /**
   * Update data on a reviewed incident
   */
  public async updateReviewedIncident(incidentId: string, reviewed: boolean) {
    const response = await axios.put(
      `${await this.getUrl()}/updateReviewedIncident`,
      { incidentId, reviewed },
      { headers: await this.headers() }
    );
    return response.data;
  }

  /**
   * Set details (Wins, Issues and Recommendations, Freeform Section) for
   * an OpsReview week
   */
  public async setOpsReviewDetails({
    date,
    wins,
    issuesAndRecommendations,
    freeform,
  }: ReviewManualDetails): Promise<
    AxiosResponse<ReviewManualDetailsDBResponse>
  > {
    const response = await axios.post(
      `${await this.getUrl()}/setOpsReviewDetails`,
      {
        date,
        wins: JSON.stringify(wins),
        issuesAndRecommendations: JSON.stringify(issuesAndRecommendations),
        freeform: JSON.stringify(freeform),
      },
      { headers: await this.headers() }
    );
    return response;
  }

  /**
   * Retrieve details for an OpsReview by data
   */
  public async getOpsReviewDetails(
    date: string
  ): Promise<AxiosResponse<ReviewManualDetailsDBResponse[]>> {
    const { data } = await axios.get(
      `${await this.getUrl()}/getOpsReviewDetails?date=${date}`,
      { headers: await this.headers() }
    );
    return data;
  }

  /**
   * Get Ownership data for a specific team
   */
  public async getTeamOwnership(team: string): Promise<AxiosResponse<any>> {
    const { data } = await axios.get(
      `${await this.getUrl()}/getTeamOwnership?team=${team}`,
      { headers: await this.headers() }
    );
    return data;
  }

  public async listOwnership(): Promise<AxiosResponse<any>> {
    const { data } = await axios.get(`${await this.getUrl()}/listOwnership`, {
      headers: await this.headers(),
    });
    return data;
  }

  /**
   * Update Ownership data for the OpsReview
   */
  public async updateOwnership({
    repo,
    hasCatalogInfo,
    archived,
    team,
    link,
  }: {
    repo: string;
    hasCatalogInfo: boolean;
    archived: boolean;
    team?: string;
    link?: string;
  }): Promise<AxiosResponse<any>> {
    const { data } = await axios.put(
      `${await this.getUrl()}/updateOwnership`,
      { repo, hasCatalogInfo, team, archived, link },
      { headers: await this.headers() }
    );
    return data;
  }

  /**
   * Retrieve violation data for all the teams
   */
  public async getTeamViolationData(): Promise<any> {
    const { data } = await axios.get(
      `${await this.getUrl()}/getTeamViolationData`,
      { headers: await this.headers() }
    );
    return data;
  }

  /**
   * Retreive OpsGenie Incidents
   */
  public async getIncidents({
    team,
    resolved = false,
    fromDaysInPast = 7,
  }: {
    team?: string;
    resolved?: boolean;
    fromDaysInPast: number;
  }) {
    let query = `resolved=${resolved}&fromDaysInPast=${fromDaysInPast}`;

    if (team) {
      query += `&team=${team}`;
    }

    const { data } = await axios.get(
      `${await this.getUrl()}/getIncidents${query ? `?${query}` : ""}`,
      { headers: await this.headers() }
    );
    return data;
  }

  /**
   * Retrieve Datadog Incidents
   */
  public async getDatadogIncidents({
    team,
    resolved = false,
    fromDaysInPast = 7,
  }: {
    team?: string;
    resolved?: boolean;
    fromDaysInPast: number;
  }) {
    let query = `resolved=${resolved}&fromDaysInPast=${fromDaysInPast}`;

    if (team) {
      query += `&team=${team}`;
    }

    const { data } = await axios.get(
      `${await this.getUrl()}/getDatadogIncidents${query ? `?${query}` : ""}`,
      { headers: await this.headers() }
    );
    return data;
  }

  /**
   * Manually process OpsReview history (create snapshot)
   */
  public async processHistory() {
    const response = await axios.post(
      `${await this.getUrl()}/processHistory`,
      {},
      { headers: await this.headers() }
    );
    return response.data;
  }
}
